import axios from "axios";
import i18next from "i18next";
import jsonwebtoken from "jsonwebtoken";
import { createPerformanceInterceptor } from "../../utils/apiMonitor";
import { createSmartDeduplicatedAxios } from "../../utils/requestDeduplication";
import { addRetryInterceptor, RETRY_CONFIGS } from "../../utils/retryMechanism";
// ✅ Import JwtService class directly to avoid circular dependency
import JwtService from "@src/@core/auth/jwt/jwtService";

const language = i18next.language;
const baseURL = `${process.env.REACT_APP_API_URL}/api/v1`;

const instance = axios.create({
  baseURL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    "Content-Type": "application/json",
    Lang: language,
  },
});

// ✅ Initialize jwtService with the axios instance AFTER creating it
// This ensures the interceptors are added to the correct axios instance
const jwtService = new JwtService({}, instance);

// ✅ REMOVED: Don't set default authorization header at module load time
// The jwtService request interceptor handles this dynamically on every request
// This was causing issues because:
// 1. It only runs once when the module loads (token might not exist yet)
// 2. It uses lowercase "authorization" while interceptor uses "Authorization"
// 3. It doesn't update when the token changes
// const getAccessToken = () =>
//   jwtService.getToken ? jwtService.getToken() : localStorage.getItem("accessToken");
// instance.defaults.headers.common["authorization"] = `Bearer ${getAccessToken()}`;

// ✅ Export jwtService so it can be used by other parts of the application
export { jwtService };

// Logout handler
const handleLogout = () => {
  console.log("🚪 Logging out user");
  localStorage.setItem("showInstallModalStep", 3);
  localStorage.removeItem("userData");
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("fetchUser");
  localStorage.removeItem("reactQueryDevtoolsSortFn");

  document.cookie.split(";").forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
  });

  setTimeout(() => {
    window.location.href = "/login";
  }, 100);
};

// ✅ FIX #6: REMOVED DUPLICATE TOKEN REFRESH LOGIC
// Token refresh is now handled exclusively by jwtService.js
// This prevents race conditions and conflicting refresh attempts
// The jwtService.js handles:
// - 401 response interception
// - Token refresh with proper queue management
// - userData object updates
// - WebSocket event notifications

// Response Interceptor - Keep only 401 handling for final logout
instance.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    // Note: 401 errors are first handled by jwtService.js interceptor
    // This is a fallback in case jwtService refresh also fails
    if (status === 401 && error.config && error.config._jwtServiceFailed) {
      console.log("❗ Token refresh failed, logging out");
      handleLogout();
    }
    return Promise.reject(error);
  }
);

// Add retry logic for failed requests
addRetryInterceptor(instance, RETRY_CONFIGS.STANDARD);

// Add performance monitoring
const performanceMonitor = createPerformanceInterceptor(instance);

// Create deduplicated axios instance for GET requests
const deduplicatedInstance = createSmartDeduplicatedAxios(instance);

// Export performance monitor for debugging
if (process.env.NODE_ENV === 'development') {
  window.apiPerformanceMonitor = performanceMonitor;
  window.apiDeduplication = {
    clearCache: deduplicatedInstance.clearCache,
    getStats: deduplicatedInstance.getCacheStats
  };
}

// Export both instances
export { deduplicatedInstance };
export default instance;
